/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import AuthIndicator from '@fenderdigital/react-auth-indicator';
import Media from 'react-media';
import isDomUsable from '@fenderdigital/dom';
import { Helmet } from 'react-helmet-async';
import { withRouter } from 'react-router-dom';
import Header from '../Header/Header';
import Library from '../../lib/library';
import FooterItems from '../../lib/menu-items';
import config from '../../config';
import CookieBanner from './CookieBanner';
import Footer from '../Footer';
import SearchPanel from '../SearchPanel';
import ConversionBanner from '../ConversionBanner/ConversionBannerContainer';
import logo from '../../assets/logo-play-horizontal.svg';

export class UnAuthenticatedLayout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSearchActive: false,
      footerItems: null,
      navItems: null,
    };
    this.handleMobileSearchClick = this.handleMobileSearchClick.bind(this);
    this.handleSearchClick = this.handleSearchClick.bind(this);
  }

  componentDidMount() {
    const { sessionCheckComplete } = this.props;
    if (sessionCheckComplete) {
      this.getNav();
      this.getFooter();
    }
  }

  componentDidUpdate(prevProps) {
    const { sessionCheckComplete: prevSessionCheckComplete } = prevProps;
    const { sessionCheckComplete } = this.props;
    if (prevSessionCheckComplete !== sessionCheckComplete && sessionCheckComplete) {
      this.getNav();
      this.getFooter();
    }
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  async getNav() {
    const navItems = await Library.getNav('play');
    this.setNav(navItems);
  }

  async getFooter() {
    const footerItems = await FooterItems.get();
    this.setFooter(footerItems);
  }

  setFooter(footerItems) {
    this.setState({ footerItems });
  }

  setNav(navItems) {
    const { menu: items } = navItems || {};
    if (items) {
      const parsedNavItems = items.filter(item => item.id !== 'my-path');
      this.setState({ navItems: parsedNavItems });
    }
  }

  getHeaderCta(pathname) {
    const { messages, intl } = this.props;
    const { formatMessage } = intl;
    const isCtaActive = pathname.indexOf('/pages') === 0 || pathname === '/';
    /* Temporary conditional for Optimize test PT-2388
     * We want to test the control version with a specific link
     * in the Start Your Free Trial button for tracking purposes
     */
    const ctaLink =
      isDomUsable() && document.isOptimizeControlTest === true && pathname === '/play'
        ? 'https://www.fender.com/connect/sign-up?ref=https://www.fender.com/play/plans?banner=06042019.ctabutton.header.homepage'
        : 'https://www.fender.com/connect/sign-up?ref=https://www.fender.com/play/plans';
    if (isCtaActive) {
      return {
        text: formatMessage(messages.cta),
        onClick: () => (window.location = ctaLink),
        wrapperClassName: 'dn db-l',
        className: 'bn',
        dataId: 'free-trial-button',
      };
    }

    return null;
  }

  /**
   * Generates a sign out url to be used as callback redirect
   * @param {string} location Usually window.location.href
   * @returns {string} a sign out href callback address
   */
  getSignOutUrl = location => {
    const isOnReactivationCta = location
      .toLowerCase()
      .trim()
      .includes('campaign-reactivation');
    let targetUrl = isDomUsable() ? location : '';
    if (isOnReactivationCta && isDomUsable())
      targetUrl = `${process.env.REACT_APP_CLIENT_FENDER_COM}/play`;
    return `${process.env.REACT_APP_CLIENT_SSO_URL}/sign-out?ref=${targetUrl}`;
  };

  handleMobileSearchClick() {
    const { history } = this.props;
    history.push('/search/');
  }

  handleSearchClick() {
    this.setState(({ isSearchActive }) => ({ isSearchActive: !isSearchActive }));
  }

  renderLogo() {
    return (
      <img
        alt=""
        className="mt2"
        src={logo}
        style={{ width: '9rem' }}
        data-id="mega-nav-fender-logo-img"
      />
    );
  }

  renderCookieBanner() {
    if (!isDomUsable()) return null;
    const isCookieBannerOpen = JSON.parse(localStorage.getItem('isCookieBannerOpen'));
    const showBanner = isCookieBannerOpen !== null ? isCookieBannerOpen : true;
    return showBanner ? <CookieBanner /> : null;
  }

  renderHeader = match => {
    const { firstName, messages, searchQuery, location } = this.props;
    const { formatMessage } = this.props.intl;
    const { navSkinny: globalNavData } = config;
    const { isSearchActive, navItems } = this.state;
    const cookieBanner = this.renderCookieBanner();
    const cta = match ? this.getHeaderCta(location.pathname) : null;
    const filteredNavItems =
      cta && navItems ? navItems.filter(n => n.id !== '14-day-free-trial') : navItems;
    return (
      <Header
        currentNav="play"
        fenderConnect={
          <AuthIndicator
            name={firstName}
            profileUrl={`${process.env.REACT_APP_CLIENT_FENDER_COM}/profile/info`}
            signedIn={firstName || false}
            signInUrl={`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-in?ref=${
              isDomUsable() ? window.location.href : ''
            }`}
            signOutUrl={this.getSignOutUrl(window.location.href)}
          />
        }
        globalNavData={globalNavData}
        isSearchActive={isSearchActive}
        logo={{
          image: this.renderLogo(),
          link: '/',
        }}
        navItems={filteredNavItems}
        onMobileSearchClick={this.handleMobileSearchClick}
        onSearchClick={this.handleSearchClick}
        promoColor="near-white"
        promoContent={cookieBanner}
        searchContent={
          <SearchPanel
            eventTypes={['click']}
            query={searchQuery}
            onCloseClick={this.handleSearchClick}
          />
        }
        titles={{
          site: formatMessage(messages.site),
          connect: formatMessage(messages.connect),
          apps: formatMessage(messages.apps),
        }}
        cta={cta}
      />
    );
  };

  renderFooter() {
    const { footerItems } = this.state;
    return (
      <div className="w-100">
        <Helmet>
          <link rel="prefetch" href={FooterItems.constructURL('footer-left')} as="fetch" />
          <link rel="prefetch" href={FooterItems.constructURL('footer-center')} as="fetch" />
          <link rel="prefetch" href={FooterItems.constructURL('footer-right')} as="fetch" />
          <link rel="prefetch" href={Library.navURL('play')} as="fetch" />
        </Helmet>
        {footerItems ? (
          <Footer
            footerLeft={footerItems[0]}
            footerCenter={footerItems[1]}
            footerRight={footerItems[2]}
          />
        ) : null}
      </div>
    );
  }

  render() {
    const { sessionCheckComplete, children } = this.props;
    const headerEl = sessionCheckComplete ? (
      <Media query={config.breakpoints.large}>{this.renderHeader}</Media>
    ) : null;
    return (
      <div className="un-authenticated-layout" data-id="un-authenticated-layout">
        {headerEl}
        {sessionCheckComplete ? <ConversionBanner /> : null}
        <div className="min-vh-100">{children}</div>
        {this.renderFooter()}
      </div>
    );
  }
}

UnAuthenticatedLayout.propTypes = {
  children: PropTypes.node,
  firstName: PropTypes.string,
  intl: intlShape.isRequired,
  messages: PropTypes.shape({}),
  searchQuery: PropTypes.string,
  sessionCheckComplete: PropTypes.bool,
  pathname: PropTypes.string.isRequired,
};

UnAuthenticatedLayout.defaultProps = {
  children: null,
  firstName: null,
  messages: {},
  searchQuery: '',
  sessionCheckComplete: null,
};

export default injectIntl(withRouter(UnAuthenticatedLayout));
